import axios from 'axios';
import axiosRetry from 'axios-retry';
import { getAccessToken, removeToken } from '../utils/helper';

const MAX_RETRIES = 3;

class ApiService {
  constructor() {
    this.instance = null;
  }

  get http() {
    return this.instance != null ? this.instance : this.initHttp();
  }

  initHttp() {
    const http = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
    });

    // Add a request interceptor to dynamically add the access token
    http.interceptors.request.use(
      (config) => {
        const token = getAccessToken(); // Fetch a new token each time
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Apply axios-retry with a dynamic token refresh on each retry
    axiosRetry(http, {
      retries: MAX_RETRIES, // Retry 3 times
      retryCondition: (error) => {
        const { response } = error;
        // Retry only for status codes 500 and 403
        return response && (response.status === 500 || response.status === 403);
      },
      retryDelay: (retryCount) => {
        return retryCount * 1000; // Exponential backoff: 1s, 2s, 3s...
      },
    });

    http.interceptors.response.use(
      (response) => response.data,
      (error) => this.handleError(error)
    );

    this.instance = http;
    return http;
  }

  request(config) {
    return this.http.request(config);
  }

  get(url, config) {
    return this.http.get(url, config);
  }

  put(url, data, config) {
    return this.http.put(url, data, config);
  }

  patch(url, data, config) {
    return this.http.patch(url, data, config);
  }

  post(url, data, config) {
    return this.http.post(url, data, config);
  }

  delete(url, config) {
    return this.http.delete(url, config);
  }

  handleError(error) {
    if (error.status === 401) {
      removeToken();
      window.location.href = '/';
    }
    // Handle error or propagate it
    return Promise.reject(error);
  }
}

export const axiosInstanceWithoutToken = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000',
});

const apiService = new ApiService();

export default apiService;
